import { themeVar } from '@/styles/theme';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled, { css } from 'styled-components';

type Placement = 'top' | 'bottom' | 'auto';

interface TooltipProps {
  children: React.ReactNode;
  title: React.ReactNode;
  arrow?: boolean;
  placement?: Placement;
  maxContentWidth?: number;
  disableTooltip?: boolean;
  className?: string;
}

export const Tooltip = ({ children, title, arrow = true, placement = 'auto', maxContentWidth, disableTooltip = false, className }: TooltipProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [arrowOffset, setArrowOffset] = useState(0);
  const [effectivePlacement, setEffectivePlacement] = useState<Placement>('auto');

  useEffect(() => {
    if (visible && wrapperRef.current && tooltipRef.current) {
      const wrapperRect = wrapperRef.current.getBoundingClientRect();
      const tooltipEl = tooltipRef.current;
      const tooltipRect = tooltipEl.getBoundingClientRect();

      const scrollY = window.scrollY;
      const scrollX = window.scrollX;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const tooltipWidth = tooltipRect.width;
      const tooltipHeight = tooltipRect.height;

      const spaceAbove = wrapperRect.top;
      const spaceBelow = viewportHeight - wrapperRect.bottom;

      let dynamicPlacement: Placement = placement;

      if (placement === 'auto') {
        dynamicPlacement = spaceAbove >= tooltipHeight + 8 || spaceAbove >= spaceBelow ? 'top' : 'bottom';
      }

      const top = dynamicPlacement === 'top' ? wrapperRect.top + scrollY - tooltipHeight - 8 : wrapperRect.bottom + scrollY + 8;

      let left = wrapperRect.left + scrollX + wrapperRect.width / 2 - tooltipWidth / 2;

      if (left + tooltipWidth > viewportWidth) {
        left = viewportWidth - tooltipWidth - 8;
      }

      if (left < 0) {
        left = 8;
      }

      const arrowLeftOffset = wrapperRect.left + wrapperRect.width / 2 - left;

      setCoords({ top, left });
      setArrowOffset(arrowLeftOffset);
      setEffectivePlacement(dynamicPlacement);
    }
  }, [visible, placement]);

  const handleMouseEnter = () => setVisible(true);
  const handleMouseLeave = () => setVisible(false);

  if (disableTooltip) {
    return <Wrapper className={className}>{children}</Wrapper>;
  }

  return (
    <>
      <Wrapper ref={wrapperRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={className}>
        {children}
      </Wrapper>
      {visible &&
        ReactDOM.createPortal(
          <Bubble
            ref={tooltipRef}
            style={{ top: coords.top, left: coords.left, ...(maxContentWidth ? { maxWidth: `${maxContentWidth}px` } : {}) }}
            placement={effectivePlacement as 'top' | 'bottom'}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {title}
            {arrow && <Arrow placement={effectivePlacement as 'top' | 'bottom'} offset={arrowOffset} />}
          </Bubble>,
          document.body
        )}
    </>
  );
};

const Wrapper = styled.div`
  display: inline-block;
  overflow-wrap: anywhere;
`;

const Bubble = styled.div<{ placement: Placement }>`
  position: absolute;
  background: ${themeVar('layer.2')};
  color: ${themeVar('font.base')};
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  z-index: 9999;
  pointer-events: auto;
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.15);
  border: 1px solid ${themeVar('border.base')};
  transform: translateY(0);

  white-space: normal;
  word-break: break-word;
`;

const Arrow = styled.div<{ placement: Placement; offset: number }>`
  position: absolute;
  left: ${({ offset }) => `${offset}px`};
  transform: translateX(-50%);
  width: 0;
  height: 0;

  ${({ placement }) =>
    placement === 'top'
      ? css`
          bottom: -6px;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 6px solid ${themeVar('border.base')};
        `
      : css`
          top: -6px;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-bottom: 6px solid ${themeVar('border.base')};
        `}
`;
